import React, { useMemo } from 'react';
import styled from 'styled-components';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useSpecialMenuCards from 'src/dispensary/hooks/use-special-menu-cards';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';

import { OfferMenuSectionV2 } from 'components/offer-menu-section';
import ContentWrapper from 'components/core/content-wrapper';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';
import { BreadCrumbs } from 'src/components/bread-crumbs';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import useUI from 'src/hooks/use-ui';

import type { SpecialsPageHeaderProps } from './specials-page-header';
import { SpecialsCategoryTiles } from './specials-category-tiles';
import { useSpecialsPageSettings } from './use-specials-page-settings';

export function SpecialsPageContentV2(): JSX.Element | null {
  const { dispensary } = useDispensary();
  const hideSalesLanguage = useHideSalesLanguage();
  const isMobile = useIsMobile();
  const { isDutchieMain } = useUI();
  const { showCategoryTiles, showSpecialDuration } = useSpecialsPageSettings();

  const ListPageHero = useRemoteComponent(RemoteComponentSlot.ListPageHero);

  const { specials, loading: specialsLoading } = useSpecialMenuCards({
    skip: !dispensary,
  });

  // "Specials" is restricted under some state regulations (e.g. MA);
  // useHideSalesLanguage swaps it for compliant copy.
  const pageTitle = hideSalesLanguage ? 'All Offers' : 'All Specials';

  const headerProps: SpecialsPageHeaderProps = useMemo(
    () => ({
      pageType: 'all-specials',
      defaultTitle: pageTitle,
    }),
    [pageTitle]
  );

  if (!dispensary) {
    return null;
  }

  return (
    <>
      <ListPageHero />
      <Container $isDutchieMain={isDutchieMain}>
        {isDutchieMain && (
          <BreadCrumbsContainer $isMobile={isMobile}>
            <BreadCrumbs includeLeftPadding={false} />
          </BreadCrumbsContainer>
        )}
        {showCategoryTiles !== false && <SpecialsCategoryTiles specials={specials} loading={specialsLoading} />}
        <OfferMenuSectionV2
          dispensary={dispensary}
          specials={specials}
          headerProps={headerProps}
          pageTitle={pageTitle}
          loading={specialsLoading}
          showDaysLeft={showSpecialDuration !== false}
        />
      </Container>
    </>
  );
}

const Container = styled(ContentWrapper)<{ $isDutchieMain: boolean }>`
  margin-top: ${({ $isDutchieMain }) => ($isDutchieMain ? '0' : '48px')};
`;

const BreadCrumbsContainer = styled.div`
  padding: 24px 16px;
`;
